import * as React from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { FiArrowRight } from "react-icons/fi";

const arrow = tv({
  base: "flex aspect-square items-center justify-center rounded-full transition-all ease-out disabled:cursor-not-allowed disabled:shadow-none disabled:ring-0",
  variants: {
    buttonType: {
      filled: "",
      outline: "",
    },
    direction: {
      right: "",
      left: "rotate-180",
    },
    color: {
      teal: [
        "bg-secondary-500 text-white",
        "hover:bg-secondary-600 hover:text-white",
        "disabled:bg-neutral-200 disabled:text-neutral-400",
      ],
      black: [
        "bg-neutral-500 text-white",
        "hover:bg-neutral-600 hover:text-white",
        "disabled:bg-neutral-200 disabled:text-neutral-400",
      ],
    },
    size: {
      lg: "size-10 text-3xl",
      sm: "size-8 text-2xl",
    },
  },
  compoundVariants: [
    {
      buttonType: "outline",
      color: "teal",
      className: [
        "border border-secondary-500 bg-transparent text-secondary-500",
        "hover:border-secondary-600 hover:bg-transparent hover:text-secondary-600",
        "disabled:border-neutral-300 disabled:bg-transparent disabled:text-neutral-300",
      ],
    },
    {
      buttonType: "outline",
      color: "black",
      className: [
        "border border-neutral-500 bg-transparent text-neutral-500",
        "hover:border-neutral-600 hover:bg-transparent hover:text-neutral-600",
        "disabled:border-neutral-300 disabled:bg-transparent disabled:text-neutral-300",
      ],
    },
    {
      buttonType: "filled",
      size: "sm",
      className: "shadow-[1.6px_6.4px_19.2px_0px_#004B4C1A]",
    },
    {
      buttonType: "filled",
      size: "lg",
      className: "shadow-[2px_8px_24px_0px_#004B4C1A]",
    },
  ],
  defaultVariants: {
    buttonType: "filled",
    color: "teal",
    size: "sm",
  },
});

type ArrowVariant = VariantProps<typeof arrow>;

export interface ArrowProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ArrowVariant {
  href?: string;
  asChild?: boolean;
}

const Arrow = React.forwardRef<HTMLButtonElement, ArrowProps>(
  (
    {
      className,
      color,
      size,
      disabled,
      href,
      asChild = !!href,
      buttonType,
      direction,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(arrow({ color, size, className, buttonType, direction }))}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {href ? (
          <Link
            href={href}
            prefetch
          >
            {<FiArrowRight />}
          </Link>
        ) : (
          <FiArrowRight />
        )}
      </Comp>
    );
  }
);
Arrow.displayName = "Arrow";

export default Arrow;
